import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";
import { Send, CheckCircle2, MessageSquare, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";

const formSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  service: z.string().min(1, "Please select a service"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type FormData = z.infer<typeof formSchema>;

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      // 1. Save to Supabase for records
      await supabase.from('Enquiry').insert([data]);

      // 2. Send email via PHP helper on Hostinger
      const res = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        setIsSuccess(true);
        reset();
        setTimeout(() => setIsSuccess(false), 5000);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-background">
        <motion.div 
          animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-primary/10 blur-[150px] rounded-full"
        />
        <motion.div 
          animate={{ x: [0, -50, 0], y: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-accent/10 blur-[150px] rounded-full"
        />
      </div>

      <div className="container relative z-10">
        <div className="max-w-5xl mx-auto glass-card border-white/5 overflow-hidden">
          <div className="grid md:grid-cols-5 h-full">
            {/* Left Info Panel */}
            <div className="md:col-span-2 bg-surface/50 p-12 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent pointer-events-none" />
              
              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative z-10"
              >
                <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-primary mb-8 border border-white/10 shadow-lg">
                  <MessageSquare size={32} />
                </div>
                <h2 className="text-4xl font-black text-white mb-6">Let's build something epic.</h2>
                <p className="text-text-muted text-lg mb-12">
                  Ready to transform your brand? Drop us a line and let's discuss your next big project.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4 text-white font-medium hover:text-primary transition-colors cursor-pointer">
                    <div className="p-3 bg-white/5 rounded-full"><ArrowRight size={18} /></div>
                    Content Strategy
                  </div>
                  <div className="flex items-center gap-4 text-white font-medium hover:text-primary transition-colors cursor-pointer">
                    <div className="p-3 bg-white/5 rounded-full"><ArrowRight size={18} /></div>
                    Social Growth
                  </div>
                  <div className="flex items-center gap-4 text-white font-medium hover:text-primary transition-colors cursor-pointer">
                    <div className="p-3 bg-white/5 rounded-full"><ArrowRight size={18} /></div>
                    Web Applications
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Form Panel */}
            <div className="md:col-span-3 p-12 bg-white/[0.02]">
              {isSuccess ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full flex flex-col items-center justify-center text-center space-y-6"
                >
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                    className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center border-4 border-green-500/20"
                  >
                    <CheckCircle2 size={48} className="text-green-500" />
                  </motion.div>
                  <div>
                    <h3 className="text-3xl font-black text-white mb-4">Message Sent!</h3>
                    <p className="text-text-muted text-lg">We'll get back to you within 24 hours.</p>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                    <label className="block text-sm font-semibold text-white/80 mb-2 uppercase tracking-widest">Name</label>
                    <input
                      {...register("name")}
                      className="w-full bg-surface/50 border border-white/10 rounded-xl px-5 py-4 text-white focus:outline-none focus:border-primary focus:bg-white/5 transition-all text-lg"
                      placeholder="John Doe"
                    />
                    {errors.name && <p className="text-red-400 text-sm mt-2 flex items-center gap-1"><ArrowRight size={14}/> {errors.name.message}</p>}
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
                    <label className="block text-sm font-semibold text-white/80 mb-2 uppercase tracking-widest">Email</label>
                    <input
                      {...register("email")}
                      type="email"
                      className="w-full bg-surface/50 border border-white/10 rounded-xl px-5 py-4 text-white focus:outline-none focus:border-primary focus:bg-white/5 transition-all text-lg"
                      placeholder="john@example.com"
                    />
                    {errors.email && <p className="text-red-400 text-sm mt-2 flex items-center gap-1"><ArrowRight size={14}/> {errors.email.message}</p>}
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                    <label className="block text-sm font-semibold text-white/80 mb-2 uppercase tracking-widest">Service</label>
                    <select
                      {...register("service")}
                      className="w-full bg-surface/50 border border-white/10 rounded-xl px-5 py-4 text-white focus:outline-none focus:border-primary focus:bg-white/5 transition-all appearance-none text-lg"
                    >
                      <option value="">Select a service...</option>
                      <option value="content">Content Creation</option>
                      <option value="social">Social Media Management</option>
                      <option value="web">Web Development</option>
                    </select>
                    {errors.service && <p className="text-red-400 text-sm mt-2 flex items-center gap-1"><ArrowRight size={14}/> {errors.service.message}</p>}
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
                    <label className="block text-sm font-semibold text-white/80 mb-2 uppercase tracking-widest">Message</label>
                    <textarea
                      {...register("message")}
                      rows={5}
                      className="w-full bg-surface/50 border border-white/10 rounded-xl px-5 py-4 text-white focus:outline-none focus:border-primary focus:bg-white/5 transition-all text-lg resize-none"
                      placeholder="Tell us about your project..."
                    />
                    {errors.message && <p className="text-red-400 text-sm mt-2 flex items-center gap-1"><ArrowRight size={14}/> {errors.message.message}</p>}
                  </motion.div>

                  <motion.button
                    initial={{ opacity: 0, y: 20 }} 
                    whileInView={{ opacity: 1, y: 0 }} 
                    viewport={{ once: true }} 
                    transition={{ delay: 0.4 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary flex items-center justify-center gap-3 py-5 text-lg rounded-xl overflow-hidden relative group"
                  >
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                    <span className="relative z-10">{isSubmitting ? "Sending..." : "Send Message"}</span>
                    {!isSubmitting && <Send size={20} className="relative z-10 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />}
                  </motion.button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
